import type { ReactNode } from "react";

import {
  IgPaper,
  IgResizableDivider,
  useResizablePanel,
} from "@intelligems/ig-theme";
import {
  Box,
  Dialog,
  DialogTitle,
  type SxProps,
  type Theme,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  ATLAS_TAKEOVER_COLUMN_SX,
  ATLAS_TAKEOVER_PAGE_SX,
} from "@src/components/AskAtlasBuildExperience/atlasTakeoverLayout";
import type { AiChatPresentation } from "@src/store/slices/global";

interface AiChatOverlayProps {
  children: ReactNode;
  fullScreen: boolean;
  modal: boolean;
  onClose: () => void;
  /** See `AiChat.presentation`. Ignored by the modal, which is always a sheet. */
  presentation: AiChatPresentation;
}

/** Default chat panel width — the panel never sizes to its message content. */
const PANEL_WIDTH = 520;
/** Bounds for the user-resizable desktop rail. */
const MIN_PANEL_WIDTH = 400;
const MAX_PANEL_WIDTH = 800;

const panelSx: SxProps<Theme> = {
  backgroundColor: (theme) => theme.igBackgroundColors.baseLightLow,
  border: 0,
  borderRadius: 0,
  boxSizing: "border-box",
  height: "100%",
  overflowY: "auto",
  padding: "10px 10px 20px",
  width: "100%",
};

/**
 * Desktop rail: the page content it sits beside already ends in its own
 * right-hand gutter, so the rail drops its left inset — otherwise the two
 * stack up and the chat reads as pushed right (left gap ≈ page gutter + rail
 * padding, right gap = rail padding alone).
 */
const railPanelSx: SxProps<Theme> = {
  ...panelSx,
  paddingLeft: 0,
};

/**
 * The chat as the page: the Atlas takeover's centered column, on the page
 * background, filling the height its host gives it.
 */
const pagePanelSx: SxProps<Theme> = {
  ...panelSx,
  ...ATLAS_TAKEOVER_COLUMN_SX,
  px: 3,
};

const visuallyHiddenSx = {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

const tabletPanelSx: SxProps<Theme> = {
  height: "100%",
  margin: 0,
  marginLeft: "auto",
  maxHeight: "100%",
  maxWidth: PANEL_WIDTH,
  width: `min(${PANEL_WIDTH}px, 100vw)`,
};

export const AiChatOverlay = ({
  children,
  fullScreen,
  modal,
  onClose,
  presentation,
}: AiChatOverlayProps) => {
  // The rail sits on the right edge of the page, so dragging its left-edge
  // divider away from the panel makes it wider.
  const panel = useResizablePanel({
    initialSize: PANEL_WIDTH,
    minSize: MIN_PANEL_WIDTH,
    maxSize: MAX_PANEL_WIDTH,
    direction: "horizontal",
    position: "right",
  });

  if (modal) {
    return (
      <Dialog
        fullScreen={fullScreen}
        open
        onClose={(_, reason) => {
          if (reason === "escapeKeyDown") {
            onClose();
          }
        }}
        slotProps={{
          paper: {
            autoFocus: true,
            sx: [panelSx, !fullScreen && tabletPanelSx],
            tabIndex: -1,
          },
        }}
      >
        <DialogTitle sx={visuallyHiddenSx}>AI chat</DialogTitle>
        {children}
      </Dialog>
    );
  }

  const isPage = presentation === "page";

  // One element tree for both presentations, restyled rather than swapped:
  // the rule builder flips from page to rail mid-build, and remounting the
  // children would tear down the chat — and the stream running in it.
  return (
    <Grid
      container
      sx={[
        {
          flexShrink: 0,
          height: "100%",
          minWidth: 0,
          // Anchors the resize handle overlaid on the panel's left edge.
          position: "relative",
          width: isPage ? "100%" : panel.size,
        },
        isPage && ATLAS_TAKEOVER_PAGE_SX,
      ]}
    >
      {isPage ? null : (
        <Box
          sx={{
            bottom: 0,
            display: "flex",
            // Nudge left so the grab bar straddles the panel's edge instead of
            // sitting inside its padding.
            left: -6,
            position: "absolute",
            top: 0,
            zIndex: 1,
          }}
        >
          <IgResizableDivider
            direction="horizontal"
            onMouseDown={panel.handleMouseDown}
            isResizing={panel.isResizing}
            handleLength={64}
          />
        </Box>
      )}
      <IgPaper sx={isPage ? pagePanelSx : railPanelSx}>{children}</IgPaper>
    </Grid>
  );
};
